import { USER_DATA_KEYS } from '../constants/storageKeys';

/**
 * TTL map used by getCalendarSyncStatus (mirrors App.js logic).
 */
const ERROR_TTL = {
  404: 60 * 60 * 1000,      // 1 heure
  401: 5 * 60 * 1000,        // 5 minutes
  403: 5 * 60 * 1000,        // 5 minutes
};

/**
 * Returns the current Google Calendar sync status for today's date.
 * @returns {{ status: 'ok'|'error'|'pending', errorCode?: number, nextRetryMs?: number }}
 */
export function getCalendarSyncStatus(dateStr) {
  const now = Date.now();
  const cacheKey = `google_sync_${dateStr}`;
  const errorKey = `google_sync_error_${dateStr}`;

  const lastSync = localStorage.getItem(cacheKey);
  if (lastSync && (now - parseInt(lastSync)) < 5 * 60 * 1000) {
    return { status: 'ok' };
  }

  const errorRaw = localStorage.getItem(errorKey);
  if (errorRaw) {
    try {
      const { timestamp, status, retryCount = 0 } = JSON.parse(errorRaw);
      let ttlMs = ERROR_TTL[status];
      if (!ttlMs) {
        ttlMs = status === 429
          ? Math.min(Math.pow(2, retryCount) * 2 * 60 * 1000, 32 * 60 * 1000)
          : 2 * 60 * 1000;
      }
      const nextRetryMs = Math.max(0, timestamp + ttlMs - now);
      return { status: 'error', errorCode: status, nextRetryMs };
    } catch {
      return { status: 'error', errorCode: null, nextRetryMs: 0 };
    }
  }

  return { status: 'pending' };
}

/**
 * Clears all Google Calendar sync cache entries so the next import is forced.
 */
export function forceClearCalendarCache() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('google_sync_')) keysToRemove.push(key);
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch (e) {
    console.warn('forceClearCalendarCache: error', e);
  }
}

/**
 * Removes all user-specific data from localStorage.
 * Preserves device-level preferences (theme, local mode, hasVisited).
 * Idempotent — safe to call multiple times.
 */
export function clearAllUserData() {
  // 1. Clear all static user keys
  USER_DATA_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`clearAllUserData: could not remove "${key}"`, e);
    }
  });

  // 2. Clear dynamic calendar sync keys (pattern: google_sync_YYYY-MM-DD)
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('google_sync_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('clearAllUserData: could not clear dynamic calendar keys', e);
  }
}
